<template>
  <ContentView class="ActualitesView">
    <div class="ActualitesView-buttons-container">
      <b-button
        v-if="isAnimateur && !isObservateurNational && !graeId"
        class="button is-primary mr-2"
        outlined
        @click="manageActualiteType"
      >
        {{ $t("actualitesPage.manage-actualite-type") }}
      </b-button>
      <b-button
        v-if="isAnimateur && !isObservateurNational"
        class="button is-primary"
        outlined
        @click="createNewActualite"
      >
        {{ graeId ? $t("actualitesPage.create-regional") : $t("actualitesPage.create-national") }}
      </b-button>
    </div>
    <TableFilterView
      :columns="columns"
      :initial-filters="activeFilters"
      :show-export="false"
      :show-custom-columns="false"
      @update-active-filters="updateActiveFilters"
      @update-visible-columns="updateVisibleColumns"
    />
    <b-table
      :ref="tableRef"
      :striped="config.isStriped"
      :hoverable="config.isHoverable"
      :debounce-search="config.searchDebounce"
      :aria-next-label="config.nextLabel()"
      :aria-previous-label="config.previousLabel()"
      :aria-page-label="config.pageLabel()"
      :aria-current-label="config.currentPageLabel()"
      :per-page="config.perPage"
      :sticky-header="config.stickyHeader"
      :current-page="currentPagedSortedParameter.pageNumber"
      :pagination-position="config.paginationPosition"
      paginated
      backend-pagination
      :sort-icon="config.sortIcon"
      :sort-icon-size="config.sortIconSize"
      backend-sorting
      sort-multiple
      :sort-multiple-data="buefySortingPriority"
      :total="total"
      :data="actualites"
      :loading="isLoading"
      @sort="onSort"
      @sorting-priority-removed="sortingPriorityRemoved"
      @page-change="onPageChange"
    >
      <b-table-column
        v-for="column in columns"
        :key="column.field"
        v-bind="column"
        :visible="column.visible"
      >
        <template #default="props">
          <div class="clickable" @click="openAcualitesModal($event, props.row['id'])">
            <div v-if="column.field === 'dateCreation'">
              {{ formatDate(props.row["dateCreation"]) }}
            </div>
            <div v-if="column.field === 'audience'">
              {{ props.row.grae ? $t("actualitesPage.regionale") : $t("actualitesPage.nationale") }}
            </div>
            <div v-if="column.field === 'type'">
              {{ props.row["type"].label }}
            </div>
            <div v-if="column.field === 'nomAuteur'">
              {{ props.row["auteur"].nom }}
            </div>
            <div v-if="column.field === 'prenomAuteur'">
              {{ props.row["auteur"].prenom }}
            </div>
            <div v-if="column.field === 'courrielAuteur'">
              {{ props.row["auteur"].courriel }}
            </div>
            <div v-if="column.field === 'dateDebutPublication'">
              {{ formatDate(props.row["dateDebutPublication"]) }}
            </div>
            <div v-if="column.field === 'dateFinPublication'">
              {{ formatDate(props.row["dateFinPublication"]) }}
            </div>
            <div v-if="column.field === 'action'">
              <b-button
                v-if="isAnimateur && (props.row.grae || isNational)"
                type="is-danger is-outlined"
                icon-left="trash"
                :title="$t('delete')"
                @click="deleteActualite($event, props.row)"
              />
            </div>
            <div v-if="!column.custom" class="clickable">
              <div>{{ props.row[column.field] }}</div>
            </div>
          </div>
        </template>
      </b-table-column>
    </b-table>
    <ActualitesModalView
      :actualite="actualiteSelected"
      :actualite-types="actualiteTypes"
      :open="openActualitesModal"
      :read-only="
        !(isNational || (isAnimateur && (actualiteSelected.grae || !actualiteSelected.id))) ||
        isObservateurNational
      "
      @close="openActualitesModal = false"
      @close-refresh="closeActualitesModalAndRefresh"
    />
    <ActualiteTypesModalView
      :open="openActualiteTypesModal"
      @close="closeActualitesTypesModal"
      @close-refresh="closeActualitesTypesModal"
    />
  </ContentView>
</template>

<script lang="ts">
import { defineComponent } from "vue"
import ContentView from "@/views/commons/ContentView.vue"
import TableFilterView from "@/views/tables/TableFilterView.vue"
import {
  ActualiteDTO,
  ActualiteTypeDTO,
  PaginationOrder,
  PaginationResult,
} from "@/model/bean/GeneratedDTOs"
import { LoginService } from "@/services/rest/login/LoginService"
import Column from "@/views/tables/Column"
import ActualitesModalView from "@/views/grae/ActualitesModalView.vue"
import ActualiteTypesModalView from "@/views/grae/ActualiteTypesModalView.vue"
import { InfoReportingService } from "@/services/log/InfoReportingService"
import { ActualiteService } from "@/services/rest/actualite/ActualiteService"
import { useTableView } from "@/hooks/useTableView"
import { SortOrder } from "@/model/SortOrder"
import { PROFIL_UTILISATEUR } from "@/model/bean/EnumHelper"
import { UsersService } from "@/services/rest/users/UsersService"
import { BButton, BTable, BTableColumn } from "buefy"

import DateUtils from "@/services/DateUtils"

export default defineComponent({
  name: "ActualitesView",
  components: {
    ContentView,
    TableFilterView,
    ActualitesModalView,
    ActualiteTypesModalView,
    BButton,
    BTable,
    BTableColumn,
  },
  props: {
    graeId: {
      type: String,
      required: false,
      default: undefined,
    },
  },
  data() {
    return {
      loginService: LoginService.INSTANCE,
      actualiteService: ActualiteService.INSTANCE,
      infoReportingService: InfoReportingService.INSTANCE,

      isAnimateur: false,
      isNational: false,
      isObservateurNational: false,
      actualites: [] as ActualiteDTO[],
      actualiteTypes: [] as ActualiteTypeDTO[],
      openActualitesModal: false,
      openActualiteTypesModal: false,
      actualiteSelected: new ActualiteDTO(),
      // Hook sera initialisé dans created()
      tableView: null as any,
    }
  },
  computed: {
    // Propriétés réactives du tableau
    tableRef(): string {
      return this.tableView?.state.tableRef || ""
    },

    isLoading(): boolean {
      return this.tableView?.state.isLoading || false
    },

    columns(): Array<Column> {
      return this.tableView?.state.columns || []
    },

    currentPagedSortedParameter(): any {
      return this.tableView?.state.currentPagedSortedParameter || null
    },

    buefySortingPriority(): Array<any> {
      return this.tableView?.state.buefySortingPriority || []
    },

    total(): number {
      return this.tableView?.state.total || 0
    },

    config(): any {
      return this.tableView?.state.config || null
    },

    perPage(): number {
      return this.tableView?.state.perPage || 20
    },

    activeFilters(): Array<any> {
      return this.tableView?.state.activeFilters || []
    },
  },
  created(): void {
    // Initialisation du hook useTableView
    this.tableView = useTableView({
      router: this.$router,
      route: this.$route,
      i18n: this.$i18n,
      getInitialColumns: this.getInitialColumns.bind(this),
      getDefaultSort: this.getDefaultSort.bind(this),
      getInitialActiveFilters: () => [],
      reloadData: this.reloadData.bind(this),
      reloadDataWhenMounted: true,
    })

    const loggedUser = this.loginService.getLoggedUser()

    if (!loggedUser) {
      return
    }

    this.isNational = UsersService.INSTANCE.hasAnyProfile(loggedUser, [
      PROFIL_UTILISATEUR.SUPER_ADMINISTRATEUR,
      PROFIL_UTILISATEUR.ACCOMPAGNATEUR_NATIONAL,
      PROFIL_UTILISATEUR.OBSERVATEUR_NATIONAL,
    ])

    this.isAnimateur =
      this.isNational ||
      (loggedUser.profil === PROFIL_UTILISATEUR.ANIMATEUR_REGIONAL &&
        ((loggedUser.grae !== undefined && loggedUser.grae.id === this.graeId) ||
          (loggedUser.instructedGraes &&
            this.graeId !== undefined &&
            loggedUser.instructedGraes.indexOf(this.graeId) !== -1)))

    this.isObservateurNational = loggedUser.profil === PROFIL_UTILISATEUR.OBSERVATEUR_NATIONAL

    this.refreshInitialColumns()
  },
  mounted(): void {
    this.tableView?.initMounted()
  },
  methods: {
    // Réexposer les méthodes du tableau
    updateVisibleColumns(newVisibleColumnList: Array<Column>): void {
      this.tableView?.updateVisibleColumns(newVisibleColumnList)
    },

    onPageChange(newPage: string | number): void {
      const pageNum = typeof newPage === "string" ? parseInt(newPage) : newPage
      this.tableView.onPageChange(pageNum)
    },

    onSort(field: string | undefined, order: SortOrder): void {
      if (field) {
        this.tableView.onSort(field, order)
      }
    },

    sortingPriorityRemoved(field: string | undefined): void {
      if (field) {
        this.tableView.sortingPriorityRemoved(field)
      }
    },

    updateActiveFilters(newFilters: any[]): void {
      this.tableView?.updateActiveFilters(newFilters)
    },

    refreshInitialColumns(): void {
      this.tableView?.refreshInitialColumns()
    },

    updateTotal(total: number): void {
      this.tableView?.updateTotal(total)
    },

    async reloadData(): Promise<void> {
      try {
        // Recharge tous les types d'actualités
        await this.reloadActualiteTypes()
        // Recharge toutes les actualités
        let paginatedActualitesList: PaginationResult<ActualiteDTO> = new PaginationResult()

        if (this.graeId) {
          paginatedActualitesList = await this.actualiteService.getAllActualitesForGRAE(
            this.graeId,
            this.currentPagedSortedParameter,
            this.activeFilters,
          )
        }

        if (!this.graeId && this.isNational) {
          paginatedActualitesList = await this.actualiteService.getActualitesNationalesOnly(
            this.currentPagedSortedParameter,
            this.activeFilters,
          )
        }

        this.actualites = paginatedActualitesList.elements
        this.updateTotal(paginatedActualitesList.count)
      } catch (e: any) {
        // Silent catch
      }
    },

    getInitialColumns(): Array<Column> {
      const initialColumns: Array<Column> = [
        {
          field: "dateDebutPublication",
          label: this.$t("actualitesPage.date-debut").toString(),
          sortable: true,
          backEndSearchable: false,
          visible: true,
          custom: true,
          canBeAdded: true,
        },
        {
          field: "dateFinPublication",
          label: this.$t("actualitesPage.date-fin").toString(),
          sortable: false,
          backEndSearchable: false,
          visible: true,
          custom: true,
          canBeAdded: true,
        },
        {
          field: "audience",
          label: this.$t("actualitesPage.audience").toString(),
          sortable: false,
          backEndSearchable: false,
          visible: true,
          custom: true,
          canBeAdded: true,
        },
        {
          field: "type",
          label: this.$t("actualitesPage.type").toString(),
          sortable: false,
          backEndSearchable: false,
          visible: true,
          custom: true,
          canBeAdded: true,
        },
        {
          field: "titre",
          label: this.$t("actualitesPage.actu-title").toString(),
          sortable: false,
          backEndSearchable: true,
          visible: true,
          custom: false,
          canBeAdded: true,
        },
        {
          field: "nomAuteur",
          label: this.$t("actualitesPage.author-name").toString(),
          sortable: false,
          backEndSearchable: false,
          visible: true,
          custom: true,
          canBeAdded: true,
        },
        {
          field: "prenomAuteur",
          label: this.$t("actualitesPage.author-surname").toString(),
          sortable: false,
          backEndSearchable: false,
          visible: true,
          custom: true,
          canBeAdded: true,
        },
        {
          field: "courrielAuteur",
          label: this.$t("actualitesPage.author-mail").toString(),
          sortable: false,
          backEndSearchable: false,
          visible: true,
          custom: true,
          canBeAdded: true,
        },
        {
          field: "dateCreation",
          label: this.$t("actualitesPage.date-creation").toString(),
          sortable: true,
          backEndSearchable: false,
          visible: true,
          custom: true,
          canBeAdded: true,
        },
      ]

      if (this.isAnimateur && !this.isObservateurNational) {
        initialColumns.push({
          field: "action",
          label: this.$t("projetsListPage.actions").toString(),
          sortable: false,
          backEndSearchable: false,
          visible: true,
          custom: true,
          canBeAdded: true,
        })
      }
      return initialColumns
    },

    getDefaultSort(): Array<PaginationOrder> {
      const defaultSort = new PaginationOrder()
      defaultSort.clause = "dateDebutPublication"
      defaultSort.desc = true
      return [defaultSort]
    },

    createNewActualite(): void {
      this.actualiteSelected = new ActualiteDTO()
      this.openActualitesModal = true
    },

    manageActualiteType(): void {
      this.openActualiteTypesModal = true
    },

    openAcualitesModal(event: Event, actualiteId: string): void {
      event.stopPropagation()
      this.actualiteSelected =
        this.actualites.find((a) => a.id === actualiteId) || new ActualiteDTO()
      this.openActualitesModal = true
    },

    formatDate(date: Date): string {
      return DateUtils.formatDate(date)
    },

    deleteActualite(event: Event, actuToDelete: ActualiteDTO): void {
      event.stopPropagation()
      this.infoReportingService.dialog(
        this.$t("graePage.confirmation").toString(),
        this.$t("actualitesPage.delete-message").toString(),
        this.$t("actualitesPage.confirm-delete").toString(),
        () => this.doDeleteActualite(actuToDelete),
        "is-danger",
      )
    },

    async doDeleteActualite(actuToDelete: ActualiteDTO): Promise<void> {
      try {
        if (actuToDelete.grae && this.isAnimateur) {
          await this.actualiteService.deleteActualiteForGRAE(actuToDelete, actuToDelete.grae)
        }

        if (!actuToDelete.grae && this.isNational) {
          await this.actualiteService.deleteActualiteNationale(actuToDelete)
        }
      } catch (e: any) {
        this.infoReportingService.error(
          this.$t("actualitesPage.error-delete").toString(),
          e as Error,
        )
      }
      await this.tableView?.reload()
    },

    async closeActualitesTypesModal(): Promise<void> {
      this.openActualiteTypesModal = false
      await this.tableView?.reload()
    },

    async closeActualitesModalAndRefresh(actualite: ActualiteDTO): Promise<void> {
      this.openActualitesModal = false
      try {
        if (actualite.id) {
          if (actualite.grae && this.isAnimateur) {
            await this.actualiteService.updateActualiteForGRAE(actualite, actualite.grae)
          }

          if (!actualite.grae && this.isNational) {
            await this.actualiteService.updateActualiteNationale(actualite)
          }
        } else {
          if (this.graeId && this.isAnimateur) {
            await this.actualiteService.createActualiteForGRAE(actualite, this.graeId)
          }

          if (!this.graeId && this.isNational) {
            await this.actualiteService.createActualiteNationale(actualite)
          }
        }
      } catch (e: any) {
        this.infoReportingService.error(this.$t("actualitesPage.error").toString(), e as Error)
      }
      await this.tableView?.reload()
    },

    async reloadActualiteTypes(): Promise<void> {
      this.actualiteTypes = await this.actualiteService.getAllActualiteTypes()
    },
  },
})
</script>

<style lang="scss">
.ActualitesView-buttons-container {
  display: flex;
  justify-content: flex-end;
}

#app {
  .ActualitesView {
    .b-table .table-wrapper.has-sticky-header {
      // Utilisation de la hauteur maximum pour le wrapper des tableaux avec entêtes fixes, solution peu élégante
      // Hauteur de l'écran - hauteur de l'entête - hauteur du titre - hauteur des boutons - hauteur de la rangée de bouton supplémentaire - hauteur du footer
      min-height: calc(100vh - 80px - 104px - 60px - 44px - 44px - 80px);
    }
  }
}
</style>
